import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Video, ChevronUp, ChevronDown, Edit2, Trash2, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageLayout from '../components/PageLayout';

const CATEGORIES = [
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'admin', label: 'Admin Tasks' },
  { value: 'participants', label: 'Participant Management' },
  { value: 'trips', label: 'Trip Planning' },
  { value: 'inventory', label: 'Inventory & Bags' },
  { value: 'general', label: 'General' },
];

/**
 * Convert various video URLs to embeddable format.
 * Supports: Loom, YouTube, Vimeo, and direct embed URLs.
 */
const getEmbedUrl = (url) => {
  if (!url) return null;
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Already an embed URL or direct video
  return url;
};

const VideoCard = ({ video, isAdmin, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const embedUrl = getEmbedUrl(video.video_url);
  const category = CATEGORIES.find(c => c.value === video.category);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col">
        {/* Video embed */}
        <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={embedUrl}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Info */}
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold text-slate-800">{video.title}</h3>
                {category && (
                  <Badge variant="outline" className="text-xs">{category.label}</Badge>
                )}
              </div>
              {video.description && (
                <p className="text-sm text-slate-500 mt-1">{video.description}</p>
              )}
            </div>

            {/* Admin controls */}
            {isAdmin && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onMoveUp(video.video_id)} disabled={isFirst} title="Move up">
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onMoveDown(video.video_id)} disabled={isLast} title="Move down">
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(video)} title="Edit">
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => onDelete(video.video_id)} title="Delete">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

const HowToPage = () => {
  const { isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', video_url: '', category: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchVideos = useCallback(async () => {
    try {
      const endpoint = isAdmin ? '/how-to/all' : '/how-to';
      const res = await api.get(endpoint);
      setVideos(res.data || []);
    } catch {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleSave = async () => {
    if (!form.title || !form.video_url) {
      toast.error('Title and video URL are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingVideo) {
        await api.put(`/how-to/${editingVideo.video_id}`, form);
        toast.success('Video updated');
      } else {
        await api.post('/how-to', form);
        toast.success('Video added');
      }
      setShowAdd(false);
      setEditingVideo(null);
      setForm({ title: '', description: '', video_url: '', category: 'general' });
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setForm({
      title: video.title,
      description: video.description || '',
      video_url: video.video_url,
      category: video.category || 'general',
    });
    setShowAdd(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`/how-to/${videoId}`);
      toast.success('Deleted');
      fetchVideos();
    } catch { toast.error('Failed'); }
  };

  const handleMove = async (videoId, direction) => {
    const idx = videos.findIndex(v => v.video_id === videoId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= videos.length) return;

    const newOrder = [...videos];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    try {
      await api.put('/how-to/reorder', { video_ids: newOrder.map(v => v.video_id) });
      setVideos(newOrder);
    } catch { toast.error('Failed to reorder'); }
  };

  const filtered = filterCategory === 'all'
    ? videos
    : videos.filter(v => v.category === filterCategory);

  const headerActions = isAdmin ? (
    <Button className="rounded-full bg-rojo hover:bg-rojo-dark h-9" onClick={() => { setEditingVideo(null); setForm({ title: '', description: '', video_url: '', category: 'general' }); setShowAdd(true); }} data-testid="add-video-btn">
      <Plus className="w-4 h-4 mr-1.5" />Add Video
    </Button>
  ) : null;

  return (
    <PageLayout title="How-To Guides" headerSub="Video tutorials for using the system" headerActions={headerActions} maxWidth="max-w-4xl">
      <div className="space-y-5">
        {/* Category filter */}
        {videos.length > 3 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filterCategory === 'all' ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40'}`}
            >
              All ({videos.length})
            </button>
            {CATEGORIES.filter(c => videos.some(v => v.category === c.value)).map(c => (
              <button
                key={c.value}
                onClick={() => setFilterCategory(filterCategory === c.value ? 'all' : c.value)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filterCategory === c.value ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40'}`}
              >
                {c.label} ({videos.filter(v => v.category === c.value).length})
              </button>
            ))}
          </div>
        )}

        {/* Video list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Video className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                {videos.length === 0 ? 'No how-to videos yet' : 'No videos in this category'}
              </p>
              {isAdmin && videos.length === 0 && (
                <p className="text-sm text-slate-400 mt-1">Click "Add Video" to upload your first tutorial. Loom, YouTube, and Vimeo links are supported.</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {filtered.map((video, idx) => (
              <VideoCard
                key={video.video_id}
                video={video}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMoveUp={(id) => handleMove(id, 'up')}
                onMoveDown={(id) => handleMove(id, 'down')}
                isFirst={idx === 0}
                isLast={idx === filtered.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) { setShowAdd(false); setEditingVideo(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingVideo ? 'Edit Video' : 'Add How-To Video'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., How to Add a New Participant"
                data-testid="video-title"
              />
            </div>
            <div>
              <Label>Video URL *</Label>
              <Input
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="Paste Loom, YouTube, or Vimeo link"
                data-testid="video-url"
              />
              <p className="text-xs text-slate-400 mt-1">Supports Loom share links, YouTube, and Vimeo URLs</p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of what this video covers"
                rows={2}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="video-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Preview */}
            {form.video_url && getEmbedUrl(form.video_url) && (
              <div>
                <Label>Preview</Label>
                <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={getEmbedUrl(form.video_url)}
                    title="Preview"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditingVideo(null); }}>Cancel</Button>
            <Button className="bg-rojo hover:bg-rojo-dark" onClick={handleSave} disabled={submitting || !form.title || !form.video_url} data-testid="save-video-btn">
              {submitting ? 'Saving...' : editingVideo ? 'Update Video' : 'Add Video'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default HowToPage;
